import type { ReactNode } from 'react';
import { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/shared/utils';
import { DetailPageActions } from './detail-page-actions';
import { DetailPageHeader, type DetailPageHeaderProps } from './detail-page-header';
import { PageContainer, type PageContainerProps } from './page-container';

type DetailPageLayoutProps = {
  headerProps: DetailPageHeaderProps;
  containerProps?: Omit<PageContainerProps, 'children'>;
  children: ReactNode;
  /** Enables the fixed-bottom action bar when the header scrolls out of view. */
  floatingActions?: boolean;
  /** Extra classes applied to the content wrapper (below the header). */
  contentClassName?: string;
};

type DetailPageFloatingActionsProps = {
  enabled: boolean;
  headerProps: DetailPageHeaderProps;
  isHeaderVisible: boolean;
};

const DetailPageFloatingActions = memo(function DetailPageFloatingActions({
  enabled,
  headerProps,
  isHeaderVisible,
}: DetailPageFloatingActionsProps) {
  if (!enabled || typeof document === 'undefined') {
    return null;
  }

  const showFloatingActions = !isHeaderVisible;

  return createPortal(
    <div
      aria-hidden={!showFloatingActions}
      data-state={showFloatingActions ? 'open' : 'closed'}
      className={cn(
        'fixed inset-x-0 bottom-0 z-50 flex justify-center p-4 pointer-events-none',
        'transform-gpu will-change-transform transition-transform duration-200 ease-out',
        'data-[state=open]:translate-y-0',
        'data-[state=closed]:translate-y-full'
      )}
    >
      <div
        data-testid="detail-floating-actions"
        className={cn(
          'pointer-events-auto w-full max-w-[720px] flex justify-center',
          !showFloatingActions && 'pointer-events-none'
        )}
      >
        {showFloatingActions ? (
          <DetailPageActions
            actions={headerProps.actions}
            secondaryAction={headerProps.secondaryAction}
            primaryAction={headerProps.primaryAction}
            className="justify-center"
          />
        ) : null}
      </div>
    </div>,
    document.body
  );
});

export function DetailPageLayout({
  headerProps,
  containerProps,
  children,
  floatingActions = true,
  contentClassName,
}: DetailPageLayoutProps) {
  const headerWrapperRef = useRef<HTMLDivElement | null>(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  const hasFloatingActions = Boolean(
    headerProps.primaryAction || headerProps.secondaryAction || headerProps.actions
  );

  const enableFloatingActions = floatingActions && hasFloatingActions;

  useLayoutEffect(() => {
    if (!enableFloatingActions) {
      setIsHeaderVisible(true);
      return;
    }

    const target = headerWrapperRef.current;
    if (!target) return;

    const scrollRoot = target.closest('main.flex-1.overflow-y-auto') as HTMLElement | null;

    const computeIsVisible = () => {
      const rect = target.getBoundingClientRect();
      if (scrollRoot) {
        const rootRect = scrollRoot.getBoundingClientRect();
        return rect.bottom - rootRect.top > 8 && rect.top < rootRect.bottom;
      }
      return rect.bottom > 8;
    };

    setIsHeaderVisible(computeIsVisible());

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        const nextVisible =
          entry.isIntersecting &&
          entry.intersectionRect.height > 0 &&
          entry.intersectionRect.width > 0;
        setIsHeaderVisible(nextVisible);
      },
      {
        root: scrollRoot,
        threshold: [0, 0.01],
        rootMargin: '-8px 0px 0px 0px',
      }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [enableFloatingActions, headerProps.title]);

  const contentPaddingClassName = useMemo(() => {
    if (!enableFloatingActions) return '';
    return 'pb-28';
  }, [enableFloatingActions]);

  const showFloatingActions = enableFloatingActions && !isHeaderVisible;

  return (
    <>
      <PageContainer {...containerProps}>
        <div ref={headerWrapperRef}>
          <DetailPageHeader {...headerProps} hideActions={showFloatingActions} />
        </div>
        <div className={cn(contentPaddingClassName, contentClassName)}>{children}</div>
      </PageContainer>

      <DetailPageFloatingActions
        enabled={enableFloatingActions}
        headerProps={headerProps}
        isHeaderVisible={isHeaderVisible}
      />
    </>
  );
}
